import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';

/**
 * AppAutocomplete - Custom autocomplete input component with same functionality as AppInput
 * @param {string} placeholder - Placeholder text for input
 * @param {boolean} error - Whether the input has an error
 * @param {string} errormsg - Error message to display
 * @param {string} name - Input name (for form data and Redux state)
 * @param {string} title - Label text for input
 * @param {boolean} important - Whether input is required (shows * indicator)
 * @param {string} type - Input type (text, number, password, etc.)
 * @param {string|number} defaultValue - Default value for input
 * @param {string} specification - Additional information about input
 * @param {React.ReactNode} icon - Icon component to display
 * @param {string} prefix - Text prefix (like currency symbols)
 * @param {Array} options - Array of autocomplete options (strings or objects with label/value)
 * @param {string} optionLabel - Key to use for option display (if options are objects)
 * @param {string} optionValue - Key to use for option value (if options are objects)
 * @param {boolean} allowCustomValue - Whether to allow values not in the options list
 * @param {Function} onOptionSelect - Callback when an option is selected
 * @param {number} maxOptionsDisplay - Maximum number of options to display (default: 10)
 */
function AppAutocomplete({
  placeholder = '',
  error = false,
  name = '',
  title = '',
  important = false,
  type = 'text',
  defaultValue = '',
  specification = '',
  step = undefined,
  min = undefined,
  max = undefined,
  icon = null,
  prefix = '',
  num = false,
  options = [],
  optionLabel = 'label',
  optionValue = 'value',
  allowCustomValue = true,
  onOptionSelect = null,
  maxOptionsDisplay = 10,
  ...rest
}) {
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const dispatch = useDispatch();
  
  // Local state for autocomplete functionality
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [inputFocused, setInputFocused] = useState(false);
  
  // Refs
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const optionRefs = useRef([]);
  
  // Get input value from Redux store or use default
  const inputValue = ApiReducer.apiJson && ApiReducer.apiJson[name] !== undefined
    ? ApiReducer.apiJson[name]
    : defaultValue;

  // Convert inputValue to string for display
  const displayValue = inputValue !== null && inputValue !== undefined ? String(inputValue) : '';

  // Process options to ensure consistent format
  const processedOptions = options.map((option, index) => {
    if (typeof option === 'string') {
      return { label: option, value: option, originalIndex: index };
    }
    return {
      label: option[optionLabel] || option.label || String(option),
      value: option[optionValue] || option.value || option,
      originalIndex: index,
      ...option
    };
  });

  // Filter options based on input value
  useEffect(() => {
    if (!isOpen || !displayValue) {
      setFilteredOptions([]);
      return;
    }

    const filtered = processedOptions.filter(option =>
      option.label.toLowerCase().includes(displayValue.toLowerCase())
    ).slice(0, maxOptionsDisplay);

    setFilteredOptions(filtered);
    setHighlightedIndex(-1);
  }, [displayValue, isOpen, options, maxOptionsDisplay]);

  // Handle input change
  const handleChange = (e) => {
    let value = e.target.value;
    
    // Convert to number if input type is number
    if (type === 'number') {
      // Handle empty string or non-numeric values
      if (value === '' || value === null || value === undefined) {
        value = '';
      } else {
        // Convert to number based on step attribute
        if (step && step.includes('.')) {
          // If step has decimal places, convert to float
          const parsedValue = parseFloat(value);
          value = isNaN(parsedValue) ? '' : parsedValue;
        } else {
          // Otherwise convert to integer
          const parsedValue = parseInt(value, 10);
          value = isNaN(parsedValue) ? '' : parsedValue;
        }
      }
    }
    
    // Update Redux store with the processed value
    const updatedApiJson = {
      ...ApiReducer.apiJson,
      [name]: value
    };
    
    console.log(`Field: ${name}, Value: ${value}, Type: ${typeof value}`);
    dispatch(setApiJson(updatedApiJson));
    
    // Show dropdown when typing
    if (!isOpen && inputFocused) {
      setIsOpen(true);
    }
  };

  // Handle option selection
  const handleOptionSelect = (option) => {
    const selectedValue = option.value;
    
    // Update Redux store
    const updatedApiJson = {
      ...ApiReducer.apiJson,
      [name]: selectedValue
    };
    dispatch(setApiJson(updatedApiJson));
    
    // Close dropdown
    setIsOpen(false);
    setHighlightedIndex(-1);
    
    // Focus back to input
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Call custom callback if provided
    if (onOptionSelect) {
      onOptionSelect(option);
    }
  };

  // Handle key press for numeric inputs and navigation
  const handleKeyPress = (e) => {
    if ((name === "price" || name === "member_price" || type === "number") && type !== "number") {
      // Allow only numbers, decimal point, and control keys
      if (!/^\d*\.?\d*$/.test(e.key) && 
          e.key !== "Backspace" && 
          e.key !== "Delete" && 
          e.key !== "ArrowLeft" && 
          e.key !== "ArrowRight" &&
          e.key !== "Tab") {
        e.preventDefault();
      }
    }
    if (num) {
      console.log("e", e);
      // Allow only numbers, decimal point, and control keys when num is true
      if (!/^\d*\.?\d*$/.test(e.key) &&
          e.key !== "Backspace" &&
          e.key !== "Delete" &&
          e.key !== "ArrowLeft" &&
          e.key !== "ArrowRight" &&
          e.key !== "Tab") {
        e.preventDefault();
      }
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        } else if (!allowCustomValue && filteredOptions.length > 0) {
          handleOptionSelect(filteredOptions[0]);
        } else {
          setIsOpen(false);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        if (inputRef.current) {
          inputRef.current.blur();
        }
        break;
      case 'Tab':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [highlightedIndex]);

  // Handle blur event to ensure proper number conversion
  const handleBlur = (e) => {
    // Delay to allow option click to register
    setTimeout(() => {
      setIsOpen(false);
      setInputFocused(false);
    }, 150);

    if (type === 'number' && e.target.value !== '') {
      let value = e.target.value;
      
      // Final conversion on blur to ensure proper number format
      if (step && step.includes('.')) {
        const parsedValue = parseFloat(value);
        if (!isNaN(parsedValue)) {
          // Apply min/max constraints if provided
          let finalValue = parsedValue;
          if (min !== undefined && finalValue < parseFloat(min)) {
            finalValue = parseFloat(min);
          }
          if (max !== undefined && finalValue > parseFloat(max)) {
            finalValue = parseFloat(max);
          }
          
          const updatedApiJson = {
            ...ApiReducer.apiJson,
            [name]: finalValue
          };
          dispatch(setApiJson(updatedApiJson));
        }
      } else {
        const parsedValue = parseInt(value, 10);
        if (!isNaN(parsedValue)) {
          // Apply min/max constraints if provided
          let finalValue = parsedValue;
          if (min !== undefined && finalValue < parseInt(min)) {
            finalValue = parseInt(min);
          }
          if (max !== undefined && finalValue > parseInt(max)) {
            finalValue = parseInt(max);
          }
          
          const updatedApiJson = {
            ...ApiReducer.apiJson,
            [name]: finalValue
          };
          dispatch(setApiJson(updatedApiJson));
        }
      }
    }
  };

  // Handle focus
  const handleFocus = () => {
    setInputFocused(true);
    if (displayValue && filteredOptions.length > 0) {
      setIsOpen(true);
    }
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="mb-4 relative">
      {/* Label */}
      {title && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          {title}
          {important && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {/* Input Container with Icon and Prefix */}
      <div className="relative">
        {/* Left Icon - Fixed positioning */}
        {icon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10 flex items-center justify-center w-5 h-5">
            {icon}
          </div>
        )}
        
        {/* Prefix (like ₹ symbol) - Positioned after icon */}
        {prefix && (
          <div className={`absolute ${icon ? 'left-11' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none z-10 flex items-center`}>
            <span className="text-sm font-medium">{prefix}</span>
          </div>
        )}
        
        {/* Dropdown Indicator */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10">
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        
        {/* Custom Input */}
        <input
          ref={inputRef}
          id={name}
          type={type}
          name={name}
          value={displayValue}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          step={step}
          min={min}
          max={max}
          autoComplete="off"
          className={`w-full h-11 py-2 bg-transparent border rounded-lg transition-colors border-slate-300 hover:border-slate-400 focus:border-blue-500 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500/20 ${
            icon && prefix ? 'pl-16 pr-10' : 
            icon ? 'pl-11 pr-10' : 
            prefix ? 'pl-10 pr-10' : 
            'pl-3 pr-10'
          }`}
          {...rest}
        />
        
        {/* Dropdown */}
        {isOpen && filteredOptions.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-300 rounded-lg shadow-lg z-50 max-h-60 overflow-auto"
          >
            {filteredOptions.map((option, index) => (
              <div
                key={`${option.originalIndex}-${option.value}`}
                ref={el => optionRefs.current[index] = el}
                className={`px-3 py-2 cursor-pointer border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${
                  index === highlightedIndex ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
                onClick={() => handleOptionSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm">{option.label}</span>
                  {option.label !== String(option.value) && (
                    <span className="text-xs text-gray-500 ml-2">{String(option.value)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-500">{ApiReducer.apiJsonError[name]}</p>
      )}
      
      {/* Specification text */}
      {specification && (
        <p className="mt-1 text-xs text-gray-500">{specification}</p>
      )}
    </div>
  );
}

export default AppAutocomplete;